/**
 * Accomplishing a Trajan tile (turn step 3) and paying out its reward.
 * R-007 (matching), R-017 (rewards per category).
 */
import { drawCards } from './cards.js';
import { addVp, fail, playerOf, succeed, updatePlayer } from './internal.js';
import { MANIFESTS } from './manifests.js';
import { ACTIONS } from './types.js';
export function declineTrajanTile(state, actor, slot) {
    const tile = playerOf(state, actor).trajanSlots[slot];
    const first = state.pendingActions[0];
    if (!tile || !first || first.source !== 'tray')
        return fail('ILLEGAL', 'nothing to decline');
    return succeed({ ...state, phase: { kind: 'awaitAction', action: first.action } }, [
        { type: 'TrajanTileDeclined', player: actor, slot, tileId: tile.id },
    ]);
}
export function acceptTrajanTile(state, actor, slot, plus2Space) {
    const player = playerOf(state, actor);
    const tile = player.trajanSlots[slot];
    const first = state.pendingActions[0];
    if (!tile || !first || first.source !== 'tray')
        return fail('ILLEGAL', 'no Trajan tile is on offer');
    if (tile.kind === 'plus2') {
        if (!plus2Space || !ACTIONS.includes(plus2Space)) {
            return fail('ILLEGAL', 'choose the extra-action space that receives the +2 marker');
        }
        if (player.plus2Spaces.includes(plus2Space)) {
            return fail('ILLEGAL', `the ${plus2Space} space already carries a +2 marker`);
        }
    }
    // Free the slot. Permanent (demand) tiles move to the player's collection; others leave the game.
    const trajanSlots = [...player.trajanSlots];
    trajanSlots[slot] = null;
    const arch = player.arch === 'center' ? slot : player.arch;
    let next = updatePlayer(state, actor, (p) => ({
        ...p,
        trajanSlots,
        arch,
        trajanDemandTiles: tile.permanent ? [...p.trajanDemandTiles, tile] : p.trajanDemandTiles,
    }));
    if (!tile.permanent)
        next = { ...next, removed: { ...next.removed, trajan: [...next.removed.trajan, tile] } };
    const events = [
        { type: 'TrajanTileAccomplished', player: actor, slot, tileId: tile.id, vp: tile.vp },
    ];
    if (player.arch === 'center')
        events.push({ type: 'ArchMoved', player: actor, to: slot });
    next = addVp(next, actor, tile.vp);
    const reward = payReward(next, actor, tile, plus2Space);
    next = reward.state;
    events.push(...reward.events);
    return succeed({ ...next, phase: { kind: 'awaitAction', action: first.action } }, events);
}
function payReward(state, actor, tile, plus2Space) {
    switch (tile.kind) {
        case 'vp':
        case 'demand':
            return { state, events: [] };
        case 'cards': {
            const n = MANIFESTS.trajanRewards.cards;
            const drawn = drawCards(state, n);
            const next = updatePlayer(drawn.state, actor, (p) => ({
                ...p,
                hand: [...p.hand, ...drawn.cards],
            }));
            return {
                state: next,
                events: [...drawn.events, { type: 'CardsDrawn', player: actor, count: drawn.cards.length }],
            };
        }
        case 'workers':
        case 'legionnaires': {
            const camp = tile.kind;
            const player = playerOf(state, actor);
            const count = Math.min(tile.tokens ?? 1, player.supply); // R-020: capped by the supply
            const next = updatePlayer(state, actor, (p) => ({
                ...p,
                supply: p.supply - count,
                [camp]: p[camp] + count,
            }));
            return { state: next, events: [{ type: 'TokensDeployed', player: actor, camp, count }] };
        }
        case 'plus2': {
            const space = plus2Space;
            const next = updatePlayer(state, actor, (p) => ({
                ...p,
                plus2Spaces: [...p.plus2Spaces, space],
            }));
            return { state: next, events: [{ type: 'Plus2MarkerPlaced', player: actor, space }] };
        }
    }
}
//# sourceMappingURL=trajanTiles.js.map